import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  FormHelperText,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import RowPerPage from "../../components/RowPerPage";
import Pagination from "../../components/Pagination";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.admin.clikkle.com/api";

const CATEGORIES = [
  "Business",
  "Enterprise",
  "Personal",
  "Developer",
  "Marketing",
  "HR",
  "Finance",
  "Other",
];

const Products = () => {
  const theme = useTheme();

  // State management
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Pagination and filtering
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [keywordsFilter, setKeywordsFilter] = useState("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    logoUrl: "",
    category: "Business",
    keywords: "",
    description: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);

  // Statistics
  const [statistics, setStatistics] = useState({
    overview: {},
    categoryStats: [],
    recentProducts: [],
  });

  const [revenueData, setRevenueData] = useState([]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page,
        limit: rowsPerPage,
        search: debouncedSearch,
        category: categoryFilter,
        keywords: keywordsFilter,
      });

      const response = await axios.get(`${API_BASE_URL}/products?${params}`);
      setProducts(response.data.data.products);
      setTotalItems(response.data.data.pagination.totalItems);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch products");
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, categoryFilter, keywordsFilter]);

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/products/statistics`);
      setStatistics(response.data.data);
    } catch (err) {
      console.error("Error fetching statistics:", err);
    }
  };

  // Fetch revenue data
  const fetchRevenueData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/products/revenue`);
      setRevenueData(response.data.data);
    } catch (err) {
      console.error("Error fetching revenue data:", err);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchStatistics();
    fetchRevenueData();
  }, []);

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Product name is required";
    } else if (formData.name.length > 100) {
      errors.name = "Product name cannot exceed 100 characters";
    }

    if (formData.logoUrl && !isValidUrl(formData.logoUrl)) {
      errors.logoUrl = "Logo URL must be a valid URL format";
    }

    if (!formData.category) {
      errors.category = "Category is required";
    }

    if (formData.description && formData.description.length > 1000) {
      errors.description = "Description cannot exceed 1000 characters";
    }

    if (formData.keywords && formData.keywords.length > 200) {
      errors.keywords = "Keywords cannot exceed 200 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };

      if (editingProduct) {
        await axios.put(
          `${API_BASE_URL}/products/${editingProduct._id}`,
          formData,
          { headers }
        );
        setSuccess("Product updated successfully");
      } else {
        await axios.post(`${API_BASE_URL}/products`, formData, { headers });
        setSuccess("Product created successfully");
      }

      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
      fetchProducts();
      fetchStatistics();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save product");
      console.error("Error saving product:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deletingProduct) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(`${API_BASE_URL}/products/${deletingProduct._id}`, {
        headers,
      });
      setSuccess("Product deleted successfully");

      setShowDeleteModal(false);
      fetchProducts();
      fetchStatistics();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete product");
      console.error("Error deleting product:", err);
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV
  const handleExportCsv = async () => {
    try {
      const params = new URLSearchParams({
        search: searchTerm,
        category: categoryFilter,
        keywords: keywordsFilter,
      });

      const response = await axios.get(
        `${API_BASE_URL}/products/export/csv?${params}`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `products_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      setSuccess("Products exported successfully");
    } catch (err) {
      setError("Failed to export products");
      console.error("Error exporting products:", err);
    }
  };

  // Form helpers
  const resetForm = () => {
    setFormData({
      name: "",
      logoUrl: "",
      category: "Business",
      keywords: "",
      description: "",
    });
    setFormErrors({});
    setEditingProduct(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (product) => {
    setFormData({
      name: product.name || "",
      logoUrl: product.logoUrl || "",
      category: product.category || "Business",
      keywords: product.keywords || "",
      description: product.description || "",
    });
    setEditingProduct(product);
    setShowEditModal(true);
  };

  const openDeleteModal = (product) => {
    setDeletingProduct(product);
    setShowDeleteModal(true);
  };

  // UI Components
  const renderProductRow = (product) => (
    <TableRow key={product._id}>
      <TableCell>
        <Box display="flex" alignItems="center" gap={2}>
          {product.logoUrl ? (
            <img
              src={product.logoUrl}
              alt={product.name}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.src = "/images/ph_cube-fill.png";
              }}
            />
          ) : (
            <Box
              width={40}
              height={40}
              borderRadius={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor={theme.palette.grey[200]}
            >
              📦
            </Box>
          )}
          <Box>
            <Typography variant="subtitle2" fontWeight="medium">
              {product.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {product.category}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{product.category}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{product.keywords}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {product.numbersOfSubscribers || 0}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Box display="flex" gap={1}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => openEditModal(product)}
              disabled={loading}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => openDeleteModal(product)}
              disabled={loading}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 3, bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          Products Management
        </Typography>
        <Box display="flex" gap={2}>
          {/* <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={() => setShowStatsModal(true)}
          >
            Statistics
          </Button> */}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddModal}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.default,
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Total Products
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {statistics.overview.totalProducts?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.default,
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Total Subscribers (unique)
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {statistics.overview.totalSubscribers?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.default,
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Total revenue (lifetime)
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                ${statistics.overview.totalRevenue?.toFixed(2) || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>App Type</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="App Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Keyword"
              value={keywordsFilter}
              onChange={(e) => setKeywordsFilter(e.target.value)}
              placeholder="Search by keyword"
            />
          </Grid> */}
          {/* <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => setPage(1)}
              disabled={loading}
            >
              Search
            </Button>
          </Grid> */}
        </Grid>
      </Box>

      {/* Products Table */}
      <TableContainer
        component={Box}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
            >
              <TableCell>Product</TableCell>
              <TableCell>App Type</TableCell>
              <TableCell>Keyword</TableCell>
              <TableCell>Numbers of Subscribers</TableCell>
              <TableCell >Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    No products found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              products.map(renderProductRow)
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
      >
        <RowPerPage
          rowsPerPage={rowsPerPage}
          onRowsChange={(newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setPage(1);
          }}
        />
        <Pagination
          count={totalItems}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={(newPage) => setPage(newPage + 1)}
        />
      </Box>

      {/* Add/Edit Modal */}
      <Dialog
        open={showAddModal || showEditModal}
        onClose={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingProduct ? "Edit Product" : "Add New Product"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name *"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Logo URL"
                value={formData.logoUrl}
                onChange={(e) =>
                  setFormData({ ...formData, logoUrl: e.target.value })
                }
                error={!!formErrors.logoUrl}
                helperText={
                  formErrors.logoUrl ||
                  "Optional - Leave empty for default icon"
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.category}>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  label="Category *"
                >
                  {CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.category && (
                  <FormHelperText>{formErrors.category}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keyword"
                value={formData.keywords}
                onChange={(e) =>
                  setFormData({ ...formData, keywords: e.target.value })
                }
                error={!!formErrors.keywords}
                helperText={formErrors.keywords}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                error={!!formErrors.description}
                helperText={formErrors.description}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddModal(false);
              setShowEditModal(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? (
              <CircularProgress size={24} />
            ) : editingProduct ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deletingProduct?.name}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Statistics Modal */}
      <Dialog
        open={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Product Statistics</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Overview
              </Typography>
              <Box display="grid" gap={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Total Products:</Typography>
                  <Typography fontWeight="bold">
                    {statistics.overview.totalProducts?.toLocaleString()}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Active Products:</Typography>
                  <Typography fontWeight="bold">
                    {statistics.overview.activeProducts?.toLocaleString()}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Total Subscribers:</Typography>
                  <Typography fontWeight="bold">
                    {statistics.overview.totalSubscribers?.toLocaleString()}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Average Price:</Typography>
                  <Typography fontWeight="bold">
                    ${statistics.overview.avgPrice?.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Revenue by Category
              </Typography>
              <Box display="grid" gap={2}>
                {revenueData.map((item) => (
                  <Box
                    key={item.category}
                    display="flex"
                    justifyContent="space-between"
                  >
                    <Typography>{item.category}:</Typography>
                    <Typography fontWeight="bold">
                      ${item.totalRevenue?.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Recent Products
              </Typography>
              <TableContainer component={Box} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Subscribers</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.recentProducts.map((product) => (
                      <TableRow key={product._id}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{product.category}</TableCell>
                        <TableCell align="right">
                          {product.subscribers?.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {new Date(product.createdAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStatsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;
